import React, { Fragment, useMemo } from 'react';
import StarIcon from '../../resources/svgComponents/StarIcon';
import { AnimatedView } from '../../utils/animations';
import {
    ImageStyle,
    StyleProp,
    StyleSheet,
    TextStyle,
    View,
    ViewStyle,
} from 'react-native';
import { CustomAnimation } from 'react-native-animatable';
import shuffle from 'lodash/shuffle';

export type Star = {
    x: number;
    y: number;
    toX: number;
    toY: number;
    size: number;
};

type GenerateRandomStarsOptions = {
    numberOfStars: number;
    width: number;
    height: number;
    minSize?: number;
    maxSize?: number;
    numberOfRows?: number;
    numberOfColumns?: number;
};

type RandomStarsProps = GenerateRandomStarsOptions & {
    getAnimation?: (
        star: Star,
    ) => string | CustomAnimation<TextStyle & ViewStyle & ImageStyle>;
    enabled?: boolean;
    duration?: number;
    style?: StyleProp<ViewStyle>;
};

function generateRandomAnimatedStars(options: GenerateRandomStarsOptions) {
    const numberOfRows = options.numberOfRows || 5;
    const numberOfColumns = options.numberOfColumns || 5;
    const minSize = options.minSize || 10;
    const maxSize = options.maxSize || 20;
    const spaceWidth = options.width / numberOfColumns;
    const spaceHeight = options.height / numberOfRows;
    const spaces = [];
    const points: Star[] = [];
    for (let i = 0; i < numberOfRows; i++) {
        for (let j = 0; j < numberOfColumns; j++) {
            spaces.push({ x: j * spaceWidth, y: i * spaceHeight });
        }
    }
    const shuffledSpaces = shuffle(spaces);
    for (let i = 0; i < options.numberOfStars && i < spaces.length; i++) {
        const size = Math.random() * (maxSize - minSize) + minSize;
        const space = shuffledSpaces[i];
        const x = space.x + Math.random() * (spaceWidth - size);
        const toX = space.x + Math.random() * (spaceWidth - size);
        const y = space.y + Math.random() * (spaceHeight - size);
        const toY = space.y + Math.random() * (spaceHeight - size);
        points.push({ x, toX, y, toY, size });
    }
    return points;
}

export function RandomStars(props: RandomStarsProps) {
    const stars = useMemo(
        () =>
            generateRandomAnimatedStars({
                numberOfStars: props.numberOfStars,
                width: props.width,
                height: props.height,
                minSize: props.minSize,
                maxSize: props.maxSize,
                numberOfRows: props.numberOfRows,
                numberOfColumns: props.numberOfColumns,
            }),
        [
            props.height,
            props.maxSize,
            props.minSize,
            props.numberOfColumns,
            props.numberOfRows,
            props.numberOfStars,
            props.width,
        ],
    );

    const numberOfColumns = props.numberOfColumns || 3;
    const spaceWidth = props.width / numberOfColumns;
    const numberOfRows = props.numberOfRows || 5;
    const spaceHeight = props.height / numberOfRows;

    return (
        <Fragment>
            {stars.map((star) => (
                <AnimatedView
                    key={JSON.stringify(star)}
                    fromTop={star.y}
                    fromLeft={star.x}
                    toTop={star.toY}
                    toLeft={star.toX}
                    animation={
                        props.getAnimation
                            ? props.getAnimation(star)
                            : undefined
                    }
                    style={[
                        styles.star,
                        { top: star.y, left: star.x },
                        props.style,
                    ]}
                    enabled={props.enabled}
                    duration={props.duration}
                >
                    <StarIcon size={star.size} />
                </AnimatedView>
            ))}
            {new Array(numberOfRows).fill(0).map((_, i) => (
                <Fragment key={i}>
                    {new Array(numberOfColumns).fill(0).map((__, j) => (
                        <View
                            key={j}
                            style={{
                                top: i * spaceHeight,
                                left: j * spaceWidth,
                                width: spaceWidth,
                                height: spaceHeight,
                                borderWidth: 1,
                                borderColor: '#FFFFFF',
                                position: 'absolute',
                                zIndex: -1,
                                display: 'none',
                            }}
                        />
                    ))}
                </Fragment>
            ))}
        </Fragment>
    );
}

const styles = StyleSheet.create({
    star: {
        position: 'absolute',
    },
});
